import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { Heading4, Heading5, Body, Label } from '~/components/atom/Typography';
import Button from '~/components/atom/Button';
import TextButton from '~/components/atom/TextButton';
import Input from '~/components/molecule/Input';
import Textarea from '~/components/molecule/Textarea';
import Dropdown, { type OptionOf } from '~/components/molecule/Dropdown';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Icon from '~/components/atom/Icon';
import Divider from '~/components/atom/Divider';
import Status from '~/components/atom/Status';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import AnimatedCheckbox from '~/components/molecule/AnimatedCheckbox';
import {
  Lfa_Calendar_Type,
  type LfaCalendarV2FieldsFragment,
  type LfaGeneratedCredentialFieldsFragment,
} from '~/graphql/types';
import type { ServicesV2Action } from '../../reducers/servicesV2Reducer';
import calendarAppliesToOffice from '~/components/page/Settings/LFASettings/servicesV2/calendarAppliesToOffice';
import useOffices from '~/hooks/useOffices';
import SectionContainer from '../SectionContainer';

const text = {
  header: "Agenda's",
  addCalendar: 'Agenda toevoegen',
  nameLabel: 'Calendar naam',
  typeLabel: 'Type',
  linkLabel: 'Link',
  linkPlaceholder: 'https://...',
  noteLabel: 'Notitie (markdown)',
  notePlaceholder: 'Voeg een notitie toe...',
  credentialModeLabel: 'Inloggegevens type',
  usernameLabel: 'Gebruikersnaam',
  passwordLabel: 'Wachtwoord',
  refIdLabel: 'Gedeelde inloggegevens',
  officeIdsLabel: 'Agenda alleen beschikbaar maken voor vestigingen:',
  deleteCalendar: 'Verwijderen',
  newCalendar: 'Nieuwe agenda',
  save: 'Opslaan',
  showPassword: 'Tonen',
  hidePassword: 'Verbergen',
  noCalendars: "Geen agenda's geconfigureerd.",
  notAvailableForOffice: 'Niet beschikbaar voor deze vestiging',
  fixedToOffice: 'Gekoppeld aan vestiging',
  linkRequired: 'Link is verplicht',
  linkInvalid: 'Link moet beginnen met https://',
  credentialModeNone: 'Geen',
  credentialModeInline: 'Uniek voor deze agenda',
  credentialModeLinked: 'Gedeelde inloggegevens',
};

type CredentialMode = 'none' | 'inline' | 'linked';

const calendarTypeOptions: Array<OptionOf<Lfa_Calendar_Type>> = [
  { key: 'Google', label: 'Google', payload: Lfa_Calendar_Type.Google },
  {
    key: 'Realworks',
    label: 'Realworks',
    payload: Lfa_Calendar_Type.Realworks,
  },
  { key: 'Unknown', label: 'Overig', payload: Lfa_Calendar_Type.Unknown },
];

const credentialModeOptions: Array<OptionOf<CredentialMode>> = [
  { key: 'none', label: text.credentialModeNone, payload: 'none' },
  { key: 'inline', label: text.credentialModeInline, payload: 'inline' },
  { key: 'linked', label: text.credentialModeLinked, payload: 'linked' },
];

const getCredentialMode = (
  calendar: LfaCalendarV2FieldsFragment,
): CredentialMode => {
  if (!calendar.credentials) return 'none';
  if (calendar.credentials.__typename === 'LFA_Calendar_Credentials_Ref')
    return 'linked';
  return 'inline';
};

const calendarRequiresLink = (type: Lfa_Calendar_Type): boolean =>
  type === Lfa_Calendar_Type.Google || type === Lfa_Calendar_Type.Unknown;

const isValidCalendarLink = (link: string | null | undefined): boolean => {
  const trimmed = link?.trim();
  if (!trimmed) return false;
  return trimmed.startsWith('https://') && trimmed.length > 'https://'.length;
};

const getCalendarLinkErrors = (
  calendar: LfaCalendarV2FieldsFragment,
): Array<string> => {
  if (!calendarRequiresLink(calendar.type)) return [];
  if (!calendar.link?.trim()) return [text.linkRequired];
  if (!isValidCalendarLink(calendar.link)) return [text.linkInvalid];
  return [];
};

type Props = {
  calendars: Array<LfaCalendarV2FieldsFragment>;
  credentials: Array<LfaGeneratedCredentialFieldsFragment>;
  dispatch: React.Dispatch<ServicesV2Action>;
  selectedOfficeId?: string | null;
};

const PasswordField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  label: string;
}> = ({ value, onChange, label }) => {
  const [revealed, setRevealed] = useState(false);

  return (
    <JustificationContainer align="end" gap="xs" width="100%">
      <Input
        type={revealed ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        label={{ text: label }}
        width="100%"
      />
      <RevealButton
        size="small"
        appearance="secondary"
        label={revealed ? text.hidePassword : text.showPassword}
        onClick={() => setRevealed(!revealed)}
      />
    </JustificationContainer>
  );
};

const CalendarModal: React.FC<{
  calendar: LfaCalendarV2FieldsFragment;
  credentials: Array<LfaGeneratedCredentialFieldsFragment>;
  onUpdate: (calendar: LfaCalendarV2FieldsFragment) => void;
  onDelete: () => void;
  onClose: () => void;
}> = ({ calendar, credentials, onUpdate, onDelete, onClose }) => {
  const offices = useOffices({ userIds: [] });
  const mode = getCredentialMode(calendar);
  const modeIdx = credentialModeOptions.findIndex(o => o.payload === mode);
  const typeIdx = calendarTypeOptions.findIndex(
    o => o.payload === calendar.type,
  );

  const toggleOffice = (officeId: string) => {
    const next = calendar.officeIds.includes(officeId)
      ? calendar.officeIds.filter(id => id !== officeId)
      : [...calendar.officeIds, officeId];
    onUpdate({ ...calendar, officeIds: next });
  };

  const handleModeChange = (newMode: CredentialMode) => {
    switch (newMode) {
      case 'none':
        onUpdate({ ...calendar, credentials: null });
        break;
      case 'inline':
        onUpdate({
          ...calendar,
          credentials: {
            __typename: 'LFA_Calendar_Credentials_Inline',
            username: '',
            password: '',
          },
        });
        break;
      case 'linked':
        onUpdate({
          ...calendar,
          credentials: {
            __typename: 'LFA_Calendar_Credentials_Ref',
            id: '',
          },
        });
        break;
    }
  };

  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="560px">
        <JustificationContainer
          direction="column"
          gap="l"
          padding={['l']}
          width="100%"
        >
          <Heading5 margin={[null]}>
            {calendar.name || text.newCalendar}
          </Heading5>

          <Input
            type="text"
            value={calendar.name}
            onChange={e => onUpdate({ ...calendar, name: e.target.value })}
            label={{ text: text.nameLabel }}
            width="100%"
          />

          <Dropdown
            label={text.typeLabel}
            options={calendarTypeOptions}
            selectedOptionIdx={typeIdx}
            onChange={({ option }) =>
              onUpdate({
                ...calendar,
                type: option.payload,
                link: option.payload === 'Realworks' ? null : calendar.link,
              })
            }
          />

          {calendar.type === Lfa_Calendar_Type.Realworks ? (
            <Input
              type="text"
              value="https://crm.realworks.nl/"
              onChange={() => {}}
              label={{ text: text.linkLabel }}
              width="100%"
              disabled
            />
          ) : (
            <Input
              type="text"
              value={calendar.link ?? ''}
              onChange={e =>
                onUpdate({ ...calendar, link: e.target.value || null })
              }
              label={{ text: text.linkLabel }}
              placeholder={text.linkPlaceholder}
              width="100%"
              externalErrors={getCalendarLinkErrors(calendar)}
            />
          )}

          <JustificationContainer direction="column" gap="xxxs" width="100%">
            <Label size="s" margin={[null]}>
              {text.noteLabel}
            </Label>
            <Textarea
              name="calendar-note"
              value={calendar.note ?? ''}
              onChange={value => onUpdate({ ...calendar, note: value || null })}
              placeholder={text.notePlaceholder}
            />
          </JustificationContainer>

          <Divider margin={[null]} />

          <Dropdown
            label={text.credentialModeLabel}
            options={credentialModeOptions}
            selectedOptionIdx={modeIdx}
            onChange={({ option }) => handleModeChange(option.payload)}
          />

          {mode === 'inline' &&
            calendar.credentials?.__typename ===
              'LFA_Calendar_Credentials_Inline' && (
              <>
                <Input
                  type="text"
                  value={calendar.credentials.username}
                  onChange={e =>
                    onUpdate({
                      ...calendar,
                      credentials: {
                        ...calendar.credentials!,
                        username: e.target.value,
                      } as typeof calendar.credentials,
                    })
                  }
                  label={{ text: text.usernameLabel }}
                  width="100%"
                />
                <PasswordField
                  value={calendar.credentials.password}
                  onChange={v =>
                    onUpdate({
                      ...calendar,
                      credentials: {
                        ...calendar.credentials!,
                        password: v,
                      } as typeof calendar.credentials,
                    })
                  }
                  label={text.passwordLabel}
                />
              </>
            )}

          {mode === 'linked' &&
            calendar.credentials?.__typename ===
              'LFA_Calendar_Credentials_Ref' && (
              <Dropdown
                label={text.refIdLabel}
                options={credentials.map(c => ({
                  key: c.id,
                  label: `${c.name} (${c.username})`,
                  payload: c.id,
                }))}
                selectedOptionIdx={credentials.findIndex(
                  c =>
                    c.id ===
                    (
                      calendar.credentials as {
                        __typename: 'LFA_Calendar_Credentials_Ref';
                        id: string;
                      }
                    ).id,
                )}
                onChange={({ option }) =>
                  onUpdate({
                    ...calendar,
                    credentials: {
                      __typename: 'LFA_Calendar_Credentials_Ref',
                      id: option.payload,
                    },
                  })
                }
              />
            )}

          <Divider margin={[null]} />

          <JustificationContainer direction="column" gap="xs" width="100%">
            <Heading5 margin={[null]} fontWeight="regular">
              {text.officeIdsLabel}
            </Heading5>
            {offices.map(office => (
              <AnimatedCheckbox
                key={office.id}
                selectable
                label={<Label margin={[null]}>{office.name}</Label>}
                value={calendar.officeIds.includes(office.id)}
                onChange={() => toggleOffice(office.id)}
              />
            ))}
          </JustificationContainer>

          <Divider margin={[null]} />

          <JustificationContainer
            justification="space-between"
            align="center"
            width="100%"
          >
            <Button
              appearance="danger"
              size="small"
              label={text.deleteCalendar}
              icon="trashcan"
              onClick={() => {
                onDelete();
                onClose();
              }}
            />
            <Button
              appearance="secondary"
              size="small"
              label={text.save}
              onClick={onClose}
            />
          </JustificationContainer>
        </JustificationContainer>
      </Modal>
    </Overlay>
  );
};

const calendarTypeLabelMap: Record<Lfa_Calendar_Type, string> = {
  [Lfa_Calendar_Type.Google]: 'Google',
  [Lfa_Calendar_Type.Realworks]: 'Realworks',
  [Lfa_Calendar_Type.Unknown]: 'Overig',
};

const CalendarsSection: React.FC<Props> = ({
  calendars,
  credentials,
  dispatch,
  selectedOfficeId = null,
}) => {
  const [editingCalendarId, setEditingCalendarId] = useState<string | null>(
    null,
  );

  const editingCalendar = editingCalendarId
    ? calendars.find(c => c.id === editingCalendarId)
    : null;

  const handleAdd = () => {
    const id = uuid();
    dispatch({ type: 'V2_ADD_CALENDAR', payload: { id } });
    setEditingCalendarId(id);
  };

  const handleUpdate = (calendar: LfaCalendarV2FieldsFragment) =>
    dispatch({
      type: 'V2_UPDATE_CALENDAR',
      payload: { calendarId: calendar.id, calendar },
    });

  return (
    <SectionContainer gap="base">
      <Heading4 size="m" margin={[null]}>
        {text.header}
      </Heading4>

      {calendars.length === 0 && (
        <Body size="s" color={{ group: 'tertiary' }}>
          {text.noCalendars}
        </Body>
      )}

      <JustificationContainer
        width="100%"
        gap="s"
        style={{ display: 'flex', flexWrap: 'wrap' }}
      >
        {calendars.map(calendar => {
          const available = (() => {
            if (!selectedOfficeId) {
              return !calendar.officeIds || calendar.officeIds.length === 0;
            }
            return calendarAppliesToOffice(calendar, selectedOfficeId);
          })();

          const isFixedToOffice =
            !selectedOfficeId && !!calendar.officeIds?.length;
          const linkErrors = getCalendarLinkErrors(calendar);
          return (
            <CalendarCard
              key={calendar.id}
              $dimmed={!available}
              onClick={() => setEditingCalendarId(calendar.id)}
            >
              <JustificationContainer
                align="center"
                justification="space-between"
                width="100%"
              >
                <JustificationContainer direction="column" gap="xxxs">
                  <Label
                    size="base"
                    margin={[null]}
                    color={available ? undefined : { group: 'tertiary' }}
                  >
                    {calendar.name || text.newCalendar}
                  </Label>
                  <Body size="s" color={{ group: 'tertiary' }} margin={[null]}>
                    {calendarTypeLabelMap[calendar.type]}
                  </Body>
                </JustificationContainer>
                <Icon name="chevron-right" />
              </JustificationContainer>
              {!available && (
                <Status
                  label={text.notAvailableForOffice}
                  backgroundColor={{
                    group: 'warning',
                    variant: 'translucent',
                  }}
                  textColor={{ group: 'warning' }}
                />
              )}
              {isFixedToOffice && (
                <Status
                  label={text.fixedToOffice}
                  backgroundColor={{
                    group: 'warning',
                    variant: 'translucent',
                  }}
                  textColor={{ group: 'warning' }}
                />
              )}
              {linkErrors.length > 0 && (
                <Status
                  label={linkErrors[0]}
                  backgroundColor={{
                    group: 'danger',
                    variant: 'translucent',
                  }}
                  textColor={{ group: 'danger' }}
                />
              )}
            </CalendarCard>
          );
        })}
      </JustificationContainer>

      <TextButton
        size="small"
        label={text.addCalendar}
        icon="plus"
        underline
        onClick={handleAdd}
      />

      {editingCalendar && (
        <CalendarModal
          calendar={editingCalendar}
          credentials={credentials}
          onUpdate={handleUpdate}
          onDelete={() =>
            dispatch({
              type: 'V2_REMOVE_CALENDAR',
              payload: { calendarId: editingCalendar.id },
            })
          }
          onClose={() => setEditingCalendarId(null)}
        />
      )}
    </SectionContainer>
  );
};

const CalendarCard = styled.div<{ $dimmed?: boolean }>(
  ({ theme, $dimmed }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing('xxs')};
    flex: 0 0 auto;
    width: max-content;
    min-width: 200px;
    padding: ${theme.spacing('s')} ${theme.spacing('m')};
    background-color: ${$dimmed
      ? theme.color('tertiary', 'translucent')
      : theme.color('white')};
    border-radius: ${theme.getTokens().border.radius.m};
    box-shadow: ${theme.getTokens().boxShadow.card};
    outline: 1px solid
      ${$dimmed
        ? theme.color('warning', 'translucent')
        : theme.color('tertiary', 'light')};
    cursor: pointer;
    opacity: ${$dimmed ? 0.7 : 1};
    transition:
      transform 0.2s ease-out,
      background-color 0.15s ease;

    &:hover {
      background-color: ${theme.color('tertiary', 'translucent')};
      transform: translateY(-5px);
      opacity: 1;
    }
  `,
);

const RevealButton = styled(Button)(
  () => css`
    flex-shrink: 0;
  `,
);

export default CalendarsSection;
